import { useMemo, type ReactNode, type Ref } from "react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi } from "@acto/taskpane/core/domain";
import { useNumberFormatter } from "@acto/taskpane/core/utils/hooks/useNumberFormatter.tsx";
import { useOpenCustomer } from "@acto/taskpane/field-sales-2/navigation";
import { SearchHeaderRow } from "@acto/taskpane/field-sales-2/components/patterns/Search";
import { CustomerRevenueCard } from "@acto/taskpane/field-sales-2/components/primitives/CustomerRevenueCard";
import { LoadMoreFooter } from "@acto/taskpane/field-sales-2/components/primitives/LoadMoreFooter";
import {
  ProductTrendList,
  type ProductTrendItem,
} from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import type { ProductTrendFilter } from "@acto/gql/graphql";
import type { KpiTrend } from "@acto/taskpane/field-sales-2/data-access/useKpiTrend";
import { useTenantClientsByKpi } from "@acto/taskpane/field-sales-2/data-access/useTenantClientsByKpi";
import { toCustomerTrendItem } from "@acto/taskpane/field-sales-2/data-access/customerTrendItem";
import { rowsToAnalysisRows, useContributeChatScope } from "@acto/taskpane/core/chat-envelope";
import { ANALYSIS_ROW_LIMIT, trendItemsToAnalysisRowInputs } from "@acto/taskpane/field-sales-2/utils/chatScope";
import { FilterHeaderRow } from "./FilterHeaderRow";
import { toClientTrendsVariables } from "./productQuickFilter";

interface CustomerBreakdownViewProps {
  /** Card identity at the top (the tenant-wide "Gesamtumsatz"). */
  rootName: string;
  /** The KPI the list ranks and displays by, shared with the products breakdown. */
  selectedKpi: Kpi;
  trend: KpiTrend;
  /** Rendered as the list title — the Produktgruppen/Kunden breakdown toggle. */
  titleSlot: ReactNode;
  dateRange: { startDate: string; endDate: string };
  /** Shared search (owned by TenantAnalysis) so the nav dock and this list stay in sync. */
  searchValue: string;
  debouncedSearch: string;
  onSearchChange: (value: string) => void;
  trends?: ProductTrendFilter[];
  filterSlot?: ReactNode;
  /** Marks the top of the header so the page can reveal the nav dock once it scrolls out of view. */
  headerSentinelRef?: Ref<HTMLDivElement>;
}

/**
 * Revenue card plus the ranked-customer breakdown list — the "Kunden" counterpart to
 * [ProductAnalysisView]. Reuses the same `ProductTrendList` rows; clients are fetched by revenue for
 * the active period via `useTenantClientsByKpi`, and each row opens the customer's details.
 */
export function CustomerBreakdownView({
  rootName,
  selectedKpi,
  trend,
  titleSlot,
  dateRange,
  searchValue,
  debouncedSearch,
  onSearchChange,
  trends,
  filterSlot,
  headerSentinelRef,
}: CustomerBreakdownViewProps) {
  const t = useTranslations("fieldSalesTwo");
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const formatNumber = useNumberFormatter();
  const openCustomer = useOpenCustomer();

  const clientTrends = toClientTrendsVariables(trends);
  const clients = useTenantClientsByKpi({
    selectedKpi,
    dateRange,
    searchTerm: debouncedSearch,
    trends: clientTrends,
  });

  const items = useMemo(
    () =>
      (clients.data ?? []).map((customer) =>
        toCustomerTrendItem(customer, selectedKpi, locale, currency, formatNumber),
      ),
    [clients.data, selectedKpi, locale, currency, formatNumber],
  );

  // Ambient context: this view owns the customers-breakdown rows (TenantAnalysis contributes the rest).
  const rowsFragment = rowsToAnalysisRows(trendItemsToAnalysisRowInputs(items), { limit: ANALYSIS_ROW_LIMIT });
  useContributeChatScope("analysis", rowsFragment);

  const isFirstLoad = clients.isFetching && items.length === 0;
  const isLoadingMore = clients.isFetching && items.length > 0;
  const isSearchActive = debouncedSearch.trim().length > 0;
  const isEmpty = !isFirstLoad && items.length === 0;

  const handleItemClick = (item: ProductTrendItem) => {
    if (!item.id) return;
    openCustomer(item.id);
  };

  return (
    <>
      <CustomerRevenueCard name={rootName} titleVariant="small" trend={trend} />
      <ProductTrendList
        items={items}
        onItemClick={handleItemClick}
        isLoading={isFirstLoad}
        emptyLabel={isSearchActive ? t("search.noResults") : tAnalysis("customersEmpty")}
        footer={
          <LoadMoreFooter
            hasMore={clients.hasNextPage}
            isLoadingMore={isLoadingMore}
            onLoadMore={clients.fetchNextPage}
          />
        }
      >
        <FilterHeaderRow filterSlot={filterSlot}>
          <SearchHeaderRow
            title={titleSlot}
            placeholder={t("search.placeholder")}
            value={searchValue}
            onChange={onSearchChange}
            disabled={isEmpty && !isSearchActive}
            bareSearch
          />
        </FilterHeaderRow>
        {/* Below the header: once this scrolls under the nav the header has passed, so the dock shows. */}
        <div ref={headerSentinelRef} aria-hidden="true" />
      </ProductTrendList>
    </>
  );
}
